/**
 * DataQuality - Dashboard for monitoring data quality across all ingested documents.
 * Shows error counts by category/type/severity, plus a scrollable error list with filters.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ResolvedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PlayArrow as AuditIcon,
  Done as DoneIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { API_ENDPOINTS, ADMIN_API_KEY } from '../config/api';

// ─── Types ──────────────────────────────────────────────────────────

interface ErrorSummary {
  total_unresolved: number;
  by_category: Record<string, number>;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
}

interface DQError {
  id: number;
  document_id: string | null;
  error_category: string;
  error_type: string;
  severity: string;
  message: string;
  details: any;
  source: string | null;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
}

// ─── Helpers ────────────────────────────────────────────────────────

const API_BASE = API_ENDPOINTS.v3Health.replace('/v3/health', '');

const severityIcon: Record<string, React.ReactNode> = {
  error: <ErrorIcon fontSize="small" sx={{ color: '#d32f2f' }} />,
  warning: <WarningIcon fontSize="small" sx={{ color: '#ed6c02' }} />,
  info: <InfoIcon fontSize="small" sx={{ color: '#0288d1' }} />,
};

const categoryColor: Record<string, string> = {
  taxonomy: '#7c4dff',
  ingestion: '#d32f2f',
  validation: '#ed6c02',
  enrichment: '#0288d1',
};

function timeAgo(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Component ──────────────────────────────────────────────────────

interface Extraction {
  id: number;
  document_id: string;
  ticker: string;
  rating: string | null;
  price_target: number | null;
  rating_change: string | null;
  extraction_confidence: string;
  confidence_reason: string;
  report_date: string | null;
}

const confidenceColor: Record<string, string> = {
  green: '#4caf50',
  yellow: '#ff9800',
  red: '#d32f2f',
};

const DataQuality: React.FC = () => {
  const [summary, setSummary] = useState<ErrorSummary | null>(null);
  const [errors, setErrors] = useState<DQError[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showResolved, setShowResolved] = useState<string>('unresolved');
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState<string | null>(null);
  const [resolving, setResolving] = useState<number | null>(null);
  const [extractions, setExtractions] = useState<Extraction[]>([]);
  const [confidenceFilter, setConfidenceFilter] = useState<string>('all');

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/v3/data-quality/summary`, {
        headers: { 'X-API-Key': ADMIN_API_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ErrorSummary = await res.json();
      setSummary(data);
      setFetchError(null);
    } catch (e: any) {
      setFetchError(e.message || 'Failed to load summary');
    }
  }, []);

  const fetchErrors = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.set('category', categoryFilter);
      if (showResolved === 'resolved') params.set('resolved', 'true');
      params.set('limit', '100');

      const res = await fetch(`${API_BASE}/v3/data-quality/errors?${params}`, {
        headers: { 'X-API-Key': ADMIN_API_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setErrors(data.errors || []);
    } catch (e: any) {
      setFetchError(e.message || 'Failed to load errors');
    } finally {
      setErrorsLoading(false);
    }
  }, [categoryFilter, showResolved]);

  const fetchExtractions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (confidenceFilter !== 'all') params.set('confidence', confidenceFilter);
      params.set('limit', '50');
      const res = await fetch(`${API_BASE}/v3/data-quality/extractions?${params}`, {
        headers: { 'X-API-Key': ADMIN_API_KEY },
      });
      if (res.ok) {
        const data = await res.json();
        setExtractions(data.extractions || []);
      }
    } catch {
      // Non-critical
    }
  }, [confidenceFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSummary(), fetchErrors(), fetchExtractions()]).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  useEffect(() => {
    fetchExtractions();
  }, [fetchExtractions]);

  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchSummary(), fetchErrors()]).finally(() => setLoading(false));
  };

  const handleRunAudit = async () => {
    setAuditing(true);
    setAuditResult(null);
    try {
      const res = await fetch(`${API_BASE}/v3/data-quality/audit?apply=true`, {
        method: 'POST',
        headers: { 'X-API-Key': ADMIN_API_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAuditResult(`Audit complete: ${data.total} issues found`);
      handleRefresh();
    } catch (e: any) {
      setAuditResult(`Audit failed: ${e.message}`);
    } finally {
      setAuditing(false);
    }
  };

  const handleResolve = async (errorId: number) => {
    setResolving(errorId);
    try {
      const res = await fetch(`${API_BASE}/v3/data-quality/resolve?error_id=${errorId}`, {
        method: 'POST',
        headers: { 'X-API-Key': ADMIN_API_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Remove from local list and refresh summary
      setErrors(prev => prev.filter(e => e.id !== errorId));
      fetchSummary();
    } catch (e: any) {
      setFetchError(`Resolve failed: ${e.message}`);
    } finally {
      setResolving(null);
    }
  };

  // ── Derived stats ─────────────────────────────────────────────────
  const sortedTypes = summary
    ? Object.entries(summary.by_type).sort((a, b) => b[1] - a[1])
    : [];

  const sortedCategories = summary
    ? Object.entries(summary.by_category).sort((a, b) => b[1] - a[1])
    : [];

  // ── Render ────────────────────────────────────────────────────────
  if (loading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1A2341' }}>
            Data Quality Monitor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Taxonomy, ingestion, validation & enrichment issues across all documents
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AuditIcon />}
            onClick={handleRunAudit}
            disabled={auditing}
            color="primary"
            size="small"
          >
            {auditing ? 'Running...' : 'Run Audit'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {auditResult && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setAuditResult(null)}>{auditResult}</Alert>
      )}

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {/* Total */}
          <Paper sx={{ p: 2, minWidth: 160, textAlign: 'center', flex: '1 1 140px' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: summary.total_unresolved > 0 ? '#ed6c02' : '#2e7d32' }}>
              {summary.total_unresolved}
            </Typography>
            <Typography variant="body2" color="text.secondary">Unresolved Issues</Typography>
          </Paper>

          {/* By Category */}
          {sortedCategories.map(([cat, count]) => (
            <Paper key={cat} sx={{ p: 2, minWidth: 140, textAlign: 'center', flex: '1 1 120px', borderTop: `3px solid ${categoryColor[cat] || '#999'}` }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>{count}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{cat}</Typography>
            </Paper>
          ))}

          {/* By Severity */}
          {Object.entries(summary.by_severity).map(([sev, count]) => (
            <Paper key={sev} sx={{ p: 2, minWidth: 120, textAlign: 'center', flex: '1 1 100px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {severityIcon[sev]}
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{count}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{sev}</Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* By Type Breakdown */}
      {sortedTypes.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Issues by Type</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {sortedTypes.map(([typ, count]) => (
              <Chip
                key={typ}
                label={`${typ.replace(/_/g, ' ')} (${count})`}
                size="small"
                variant="outlined"
                onClick={() => setCategoryFilter('all')}
                sx={{ textTransform: 'capitalize' }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            <MenuItem value="taxonomy">Taxonomy</MenuItem>
            <MenuItem value="ingestion">Ingestion</MenuItem>
            <MenuItem value="validation">Validation</MenuItem>
            <MenuItem value="enrichment">Enrichment</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={showResolved}
          exclusive
          onChange={(_, val) => val && setShowResolved(val)}
          size="small"
        >
          <ToggleButton value="unresolved">Unresolved</ToggleButton>
          <ToggleButton value="resolved">Resolved</ToggleButton>
        </ToggleButtonGroup>

        {errorsLoading && <CircularProgress size={20} />}
      </Box>

      {/* Error Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 40 }}>Sev</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 160 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }}>Doc ID</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 90 }}>When</TableCell>
              {showResolved === 'resolved' ? (
                <TableCell sx={{ fontWeight: 600, width: 100 }}>Resolved By</TableCell>
              ) : (
                <TableCell sx={{ fontWeight: 600, width: 70 }}>Action</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.length === 0 && !errorsLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <ResolvedIcon sx={{ fontSize: 40, color: '#2e7d32' }} />
                    <Typography color="text.secondary">
                      {showResolved === 'resolved' ? 'No resolved errors found' : 'No unresolved issues -- all clean!'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              errors.map((err) => (
                <TableRow key={err.id} hover sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                  <TableCell>
                    <Tooltip title={err.severity}>
                      <span>{severityIcon[err.severity] || <InfoIcon fontSize="small" />}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={err.error_category}
                      size="small"
                      sx={{
                        bgcolor: categoryColor[err.error_category] || '#999',
                        color: '#fff',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {err.error_type.replace(/_/g, ' ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {err.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {err.source || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {err.document_id ? (
                      <Tooltip title={err.document_id}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            color: '#1976D2',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => window.open(`/document-pro?id=${err.document_id}`, '_blank')}
                        >
                          {err.document_id.slice(0, 8)}<OpenIcon sx={{ fontSize: 10, ml: 0.3, verticalAlign: 'middle' }} />
                        </Typography>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={err.created_at}>
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(err.created_at)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  {showResolved === 'resolved' ? (
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {err.resolved_by || '-'}
                      </Typography>
                    </TableCell>
                  ) : (
                    <TableCell>
                      <Tooltip title="Mark as resolved">
                        <span>
                          <Button
                            size="small"
                            sx={{ minWidth: 0, p: 0.5 }}
                            onClick={() => handleResolve(err.id)}
                            disabled={resolving === err.id}
                            color="success"
                          >
                            <DoneIcon fontSize="small" />
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {errors.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing {errors.length} {showResolved === 'resolved' ? 'resolved' : 'unresolved'} errors
          {categoryFilter !== 'all' && ` in ${categoryFilter}`}
        </Typography>
      )}

      {/* Extraction Confidence Section */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Extraction Confidence
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Confidence</InputLabel>
            <Select
              value={confidenceFilter}
              onChange={(e) => setConfidenceFilter(e.target.value)}
              label="Confidence"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="green">Green</MenuItem>
              <MenuItem value="yellow">Yellow</MenuItem>
              <MenuItem value="red">Red</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rating</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>PT</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Change</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {extractions.map((ext) => (
                <TableRow key={ext.id} hover>
                  <TableCell>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: confidenceColor[ext.extraction_confidence] || '#9e9e9e',
                        display: 'inline-block',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{ext.ticker}</Typography>
                  </TableCell>
                  <TableCell>{ext.rating || '-'}</TableCell>
                  <TableCell>{ext.price_target ? `$${ext.price_target}` : '-'}</TableCell>
                  <TableCell>
                    {ext.rating_change ? (
                      <Chip label={ext.rating_change} size="small" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {ext.confidence_reason || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {ext.report_date || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {extractions.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing {extractions.length} extractions
            {confidenceFilter !== 'all' && ` (${confidenceFilter} only)`}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default DataQuality;
